import React, { Component } from "react";
import Icon from "../../Common/Icon";
import connection from "../../../api/config/connection";
import purifyHTML from "../../../utils/purifyHTML";

export default class ProductPrices extends Component {
  basedOn = {
    ovchipkaartnummer: undefined,
    productId: undefined,
    ingangsdatum: undefined,
  };
  prices = [];
  updatePending = false;
  updateFailed = false;

  constructor() {
    super();
    this.state = {
      errorMessage: undefined,
    };
  }

  formValuesChanged = () => {
    if (this.updatePending) return;

    const { config, formikForm } = this.props;

    const { source, ovchipkaartnummerName, productIdName } = config;

    if (!source || !ovchipkaartnummerName || !productIdName) {
      console.error(
        "Missing source, ovchipkaartnummerName and/or productIdName properties"
      );
      return;
    }

    const errors = formikForm.errors || {};
    if (errors[ovchipkaartnummerName]) {
      this.basedOn.ovchipkaartnummer = null;
      this.prices = [];
      return;
    }

    const values = formikForm.values || {};
    const ovchipkaartnummer = values[ovchipkaartnummerName];
    const productId = values[productIdName];
    // @TODO Checken of dit ook werkt bij andere talen?
    const ingangsdatum = values["ingangsdatum"];

    if (!ovchipkaartnummer || !productId || !ingangsdatum) return;

    if (
      ovchipkaartnummer === this.basedOn.ovchipkaartnummer &&
      productId === this.basedOn.productId &&
      ingangsdatum === this.basedOn.ingangsdatum
    ) {
      return;
    }

    this.updatePrices(source, ovchipkaartnummer, productId, ingangsdatum);
  };

  updatePrices = (source, ovchipkaartnummer, productId, ingangsdatum) => {
    if (this.updatePending) return;

    this.updateFailed = false;
    this.updatePending = true;
    this.prices = [];
    this.setState({
      errorMessage: undefined,
    });

    const options = {
      method: "GET",
      url: `${source}`,
      params: {
        productId,
        ovchipkaartnummer,
        ingangsdatum,
      },
    };

    connection
      .instance()
      .request(options)
      .then((response) => {
        try {
          if (!Array.isArray(response.data))
            throw new Error("No price data or no array");

          this.updatePending = false;
          this.prices = response.data.map(
            ({ selected, label, sterwaardes, price }) => ({
              selected,
              label,
              sterwaardes,
              price,
            })
          );

          this.basedOn.ovchipkaartnummer = ovchipkaartnummer;
          this.basedOn.productId = productId;
          this.basedOn.ingangsdatum = ingangsdatum;

          this.setState({
            errorMessage: undefined,
          });
        } catch (ex) {
          this.onUpdateFailed();
        }
      })
      .catch((xhr) => {
        let { data = {} } = xhr.response || {};
        this.onUpdateFailed(data.error);
      });
  };

  onUpdateFailed(errorMessage) {
    this.updateFailed = true;
    this.updatePending = false;
    this.prices = [];

    this.setState({
      errorMessage,
    });
  }

  getPrice() {
    if (!this.prices) return undefined;

    const type = this.prices.find(({ selected }) => selected);
    if (!type) return undefined;

    const selectedSterwaarde = this.getSelectedSterwaarde();
    if (!selectedSterwaarde) return type.price;

    const sterwaarde = (type.sterwaardes || []).find(
      (sterwaarde) => sterwaarde.value === selectedSterwaarde
    );
    if (!sterwaarde) return undefined;

    return sterwaarde.price;
  }

  getSelectedSterwaarde() {
    const { config, formikForm } = this.props;
    const { sterwaardeName } = config;
    const { values = {} } = formikForm;
    return sterwaardeName ? values[sterwaardeName] : null;
  }

  updateValue() {
    const price = this.getPrice();
    const { formikForm, formikField } = this.props;

    if (formikField.value !== price)
      formikForm.setFieldValue(formikField.name, price);
  }

  componentDidUpdate() {
    this.updateValue();
  }

  render = () => {
    if (!this.updateFailed) this.formValuesChanged();

    let {
      sterwaardeName,
      noOvchipkaartnummerOrSterwaarde = "To calculate the price we need your OV-chipcard number and, if applicable, the star value.",
      updatingPricesText = "Updating the price...",
      updateFailedText = "Unable to calculate the price. Check your internet connection.",
      retryButtonText = "Recalculate price",
      noPricesText = "No price found for this subscription",
    } = this.props.config;
    noOvchipkaartnummerOrSterwaarde = purifyHTML(
      noOvchipkaartnummerOrSterwaarde
    );
    updateFailedText = purifyHTML(updateFailedText);
    noPricesText = purifyHTML(noPricesText);

    let { errorMessage = updateFailedText } = this.state;
    errorMessage = purifyHTML(errorMessage);

    const selectedSterwaarde = this.getSelectedSterwaarde();
    const fieldsFilled =
      this.basedOn.ovchipkaartnummer && (!sterwaardeName || selectedSterwaarde);
    const hasPrice = !!this.getPrice();

    return (
      <div
        className={`form__group form__price-table ${
          this.updatePending ? "form__price-table--loading" : ""
        }`}
      >
        {this.updateFailed ? (
          <React.Fragment>
            <div
              className="form__error"
              dangerouslySetInnerHTML={errorMessage}
            />
            <button type="button" onClick={this.formValuesChanged}>
              {retryButtonText}
            </button>
          </React.Fragment>
        ) : this.updatePending ? (
          <React.Fragment>
            <Icon iconName="loading-light" /> <span>{updatingPricesText}</span>
          </React.Fragment>
        ) : !fieldsFilled ? (
          <div
            className="form__warning"
            dangerouslySetInnerHTML={noOvchipkaartnummerOrSterwaarde}
          />
        ) : !hasPrice ? (
          <div className="form__error" dangerouslySetInnerHTML={noPricesText} />
        ) : (
          this.prices.map(({ label, selected, sterwaardes, price }) => {
            if (selectedSterwaarde && sterwaardes) {
              const sterwaarde =
                sterwaardes.find(
                  (sterwaarde) => sterwaarde.value === selectedSterwaarde
                ) || {};
              price = sterwaarde.price;
            }
            price = price || "-";

            return (
              <div
                key={`${label}`}
                className={`form__group form__group--has-columns ${
                  selected ? "form__price-table--selected" : ""
                }`}
              >
                <div className="form__column">{label}</div>
                <div className="form__column">{price}</div>
              </div>
            );
          })
        )}
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/FormTemplates/Field/ProductPrices.js